<script setup>
import { watch, computed, inject, ref } from 'vue'
import { useAnomaliesStore } from '@/store/anomalies'
import { useStatsStore } from '@/store/stats'
import { useRoute, useRouter } from 'vue-router'
import HealthSystemMonitoring from '../home/charts/HealthSystemMonitoring.vue'

const route = useRoute()
const router = useRouter()
const loading = computed(() => { return anomaliesStore.loadingStuff })
const anomaliesStore = useAnomaliesStore()
const statsStore = useStatsStore()
const windowTooSmall = inject('windowTooSmall')
const tempFilters = computed(() => {
  return { ...anomaliesStore.filters }
})

const applyFilter = async () => {
  Object.keys(tempFilters.value).forEach(k => { anomaliesStore.filters[k] = tempFilters.value[k] })
  await anomaliesStore.fetchAnomalies()
}

watch(() => route, async () => {
  await anomaliesStore.fetchAnomalies()
  await statsStore.initializeSystemStats()
}, { immediate: true })

const navigateToDetails = (data) => {
  router.push(`/anomalies/${data.id}`)
}

const op = ref()
const toggle = (event) => {
  op.value.toggle(event)
}

</script>

<template>
  <div class="card">
    <div
      class="flex flex-row lg:gap-0 gap-4 justify-between lg:items-center h-fit"
    >
      <div class="flex-1 md:p-4 h-full">
        <HealthSystemMonitoring
          v-model="statsStore.healthSystemMonitoring"
        />
      </div>

      <LogFilter
        v-if="!windowTooSmall"
        v-model="anomaliesStore.filters"
      />
    </div>
    <Divider v-if="!windowTooSmall" />
    <div
      v-if="loading"
      class="flex flex-1 w-full items-center justify-center"
    >
      <ProgressSpinner />
    </div>
    <div
      v-else
      class="flex-1 overflow-y-auto px-0 -mx-4 -mb-4 md:mb-0 md:mx-0 md:px-10 md:pb-4 rounded-lg bg-[--p-content-background]"
    >
      <DataTable
        v-if="!windowTooSmall"
        selection-mode="single"
        :value="anomaliesStore.anomalies"
        table-style="min-width: 50rem; width: 100%"
        scrollable
        scroll-height="flex"
        :virtual-scroller-options="{ itemSize: 47, numToleratedItems: 50 }"
        @row-select="navigateToDetails($event.data)"
      >
        <template #empty>
          No anomalies found
        </template>
        <Column
          style="min-width: 10rem"
          field="code"
          header="Status"
        >
          <template #body="{ data }">
            <Tag
              :icon="data.enddate ? 'pi pi-check': 'pi pi-exclamation-circle'"
              :severity="data.enddate ? 'success': 'danger'"
              :value="data.enddate ? 'RESOLVED': 'ONGOING'"
            />
          </template>
        </Column>
        <Column
          field="startdate"
          header="Start Date"
          style="width:200px;"
        >
          <template #body="{ data }">
            {{ new Date(data.startdate).toLocaleString() }}
          </template>
        </Column>
        <Column
          field="enddate"
          header="End Date"
          style="width: 200px;"
        >
          <template #body="{ data }">
            {{ data.enddate ? new Date(data.enddate).toLocaleString() : '' }}
          </template>
        </Column>
        <Column
          field="interval"
          header="Duration"
        >
          <template #body="{ data}">
            {{ data.formattedDuration }}
          </template>
        </Column>
      </DataTable>
      <DataTable
        v-else
        selection-mode="single"
        :value="anomaliesStore.anomalies"
        table-style="width: 100%"
        scrollable
        scroll-height="flex"
        filter-display="menu"
        :virtual-scroller-options="{ itemSize: 64.5, numToleratedItems: 50 }"
        @row-select="navigateToDetails($event.data)"
      >
        <template #empty>
          No anomalies found
        </template>
        <Column
          field="startdate"
          sortable
        >
          <template #header="{}">
            <div class="flex flex-row w-full items-center justify-between">
              <b>Anomalies</b>
              <Button
                type="button"
                icon="pi pi-filter"
                variant="text"
                @click="toggle"
              />
              <Popover ref="op">
                <LogFilter v-model="tempFilters" />
                <div class="flex flex-row justify-end pt-3">
                  <Button
                    label="Apply"
                    @click="applyFilter()"
                  />
                </div>
              </Popover>
            </div>
          </template>
          <template #body="{ data }">
            <div class="flex items-center justify-between w-full">
              <div class="flex flex-col items-center gap-2">
                <div class="flex flex-row items-center gap-2">
                  <i
                    class="pi pi-clock"
                  />
                  <div class="flex flex-row items-center h-full">
                    {{ new Date(data.startdate).toLocaleString() }}
                  </div>
                </div>
                <div class="flex flex-row items-center text-sm text-surface-500 dark:text-surface-400 w-full gap-2">
                  <i class="pi pi-stopwatch" />
                  {{ data.formattedDuration }}
                </div>
              </div>
              <Tag
                :icon="data.enddate ? 'pi pi-check': 'pi pi-exclamation-circle'"
                :severity="data.enddate ? 'success': 'danger'"
                :value="data.enddate ? 'RESOLVED': 'ONGOING'"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>
<style scoped>
.p-tabpanels {
  flex-grow: 1;
}
</style>
